/**
 * The privilege CODES a change order action requires, and the rule for using them.
 *
 * CODES, NOT DISPLAY NAMES. `users._identifier` puts the privilege CODE in the session when one
 * exists -- so the session holds `changeOrder:submit`, never "Change Order: Submit for approval".
 * Feeding a display name to hasPrivilege hides the action from EVERYONE, administrators included,
 * and it fails silently: the button is simply not there. t5 shipped that server-side twice.
 *
 * HIDING IS UX; THE GUARD IS THE BOUNDARY. Every code here is enforced in
 * cres_t1/service/change_order_privilege_guard.py, and nothing is hidden that the server would
 * allow. Gating a control the server does not refuse turns the button into decoration over an open
 * endpoint -- and the reverse, showing one the server refuses, tells the reader "yes" and then "no".
 *
 * VIEW IS ABSENT ON PURPOSE. `changeOrder:view` exists and is granted, but reads are scoped by
 * persona visibility rather than guarded by privilege, so nothing may hide on it: the sidebar entry
 * and the list always render. Adding it here would hide an app from people the server serves.
 *
 * SIGNING IS NOT HERE EITHER. Who may sign a change order comes from the approval matrix's persona
 * slots, and the approver's queue decides what it offers.
 */
export const CHANGE_ORDER = {
    RAISE: "changeOrder:raise",
    EDIT: "changeOrder:edit",
    DELETE: "changeOrder:delete",
    ADD_RECORD: "changeOrder:addRecord",
    REMOVE_RECORD: "changeOrder:removeRecord",
    SUBMIT: "changeOrder:submit",
    CLOSE: "changeOrder:close",
    VOID: "changeOrder:void",
} as const;

export type ChangeOrderPrivilege = typeof CHANGE_ORDER[keyof typeof CHANGE_ORDER];

/**
 * Keep the menu options whose privilege the caller holds.
 *
 * An option with NO privilege in the map always renders -- Refresh is not a permission question, and
 * an action that declares nothing must not be hidden by a lookup that returns undefined.
 */
export function allowedMenuOptions<T extends {value: string}>(
    options: T[],
    privilegeByValue: {[value: string]: string},
    holds: (privilege: string) => boolean,
): T[] {
    return options.filter(option => {
        const required = privilegeByValue[option.value];
        return !required || holds(required);
    });
}
